/**
 * Crash & error reporting. Sentry initializes only when VITE_SENTRY_DSN is set,
 * so local dev and forks without a DSN run clean. Everything funnels through
 * reportError() so call sites never import Sentry directly.
 */
import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
export const sentryEnabled = Boolean(dsn);

export function initSentry(): void {
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION ?? "dev",
    // Privacy: never send user text (messages, bios) — only errors + stacks.
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    beforeSend(event) {
      // Strip any request bodies defensively.
      if (event.request) delete event.request.data;
      return event;
    },
  });

  window.addEventListener("unhandledrejection", (ev) => {
    reportError(ev.reason instanceof Error ? ev.reason : new Error(String(ev.reason)));
  });
}

/** Tag events with the signed-in uid (an opaque id — no PII). */
export function setSentryUser(uid: string | null): void {
  if (!sentryEnabled) return;
  Sentry.setUser(uid ? { id: uid } : null);
}

export function reportError(e: unknown, context?: Record<string, unknown>): void {
  if (import.meta.env.DEV) console.error("[fuse]", e, context ?? "");
  if (!sentryEnabled) return;
  Sentry.captureException(e, context ? { extra: context } : undefined);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
